import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  SearchIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
} from 'lucide-angular';
import { Category } from '../../../types/category.types';
import { smartTitle } from '../../../utils/pipes/smart-capitalize';
import { CategoriesService } from '../../../services/categories.service';
import { CartService } from '../../../services/cart.service';
import { NotificationService } from '../../../services/notification.service';
import { BreakpointService } from '../../../services/breakpoint.service';

@Component({
  selector: 'allas-search-bar',

  imports: [CommonModule, LucideAngularModule],
  templateUrl: 'search-bar.html',
  styleUrls: ['./search-bar.scss'],
})
export class SearchBarComponent {
  @Input() searchQuery: string = '';
  @Output() searchChange = new EventEmitter<string>();
  @Input() divStyleClass: string = '';
  @Input() inputStyleClass: string = '';
  @Input() buttonStyleClass: string = '';
  @Input() placeholder: string = 'Encuentra repuestos y productos';
  @Input() iconStyleClass: string = '';
  @Input() isInStickyHeader: boolean = false;

  readonly searchIcon = SearchIcon;
  readonly plusIcon = PlusIcon;
  readonly minusIcon = MinusIcon;
  readonly cartIcon = ShoppingCartIcon;

  // Servicios
  private categoriesService = inject(CategoriesService);
  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  public breakpointService = inject(BreakpointService);

  // Estado para mostrar/ocultar sugerencias
  showSuggestions = signal(false);
  searchValue = signal('');
  private blurTimeout: any = null;
  private isMouseInSuggestions = false;

  // Estado local para cantidades visuales (sin afectar el carrito)
  visualQuantities = signal<Record<string, number>>({});

  // Categorías filtradas basadas en la búsqueda
  filteredCategories = computed(() => {
    const searchTerm = this.searchValue().toLowerCase().trim();

    if (!searchTerm) {
      return [];
    }

    // Usar el servicio para buscar categorías
    const results = this.categoriesService.searchCategories(searchTerm);

    // Limitar a 8 resultados para no sobrecargar la UI
    return results.slice(0, 8);
  });

  smartTitle = smartTitle;

  constructor() {
    // Efecto para mostrar/ocultar sugerencias basado en el valor de búsqueda
    effect(() => {
      const hasResults = this.filteredCategories().length > 0;
      const hasSearchTerm = this.searchValue().trim().length > 0;
      this.showSuggestions.set(hasResults && hasSearchTerm);
    });
  }

  onSearchChange(event: any): void {
    const value = event.target.value;
    this.searchValue.set(value);
    this.searchChange.emit(value);
  }

  onInputFocus(): void {
    // En mobile, redirigir a la página de búsqueda full-screen
    if (this.breakpointService.isMobile()) {
      const currentQuery = this.searchValue();
      const queryParams = currentQuery ? { q: currentQuery } : {};
      this.router.navigate(['/search'], { queryParams });
      return;
    }

    // En desktop, mantener comportamiento actual
    // Limpiar timeout si existe
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }

    if (
      this.searchValue().trim().length > 0 &&
      this.filteredCategories().length > 0
    ) {
      this.showSuggestions.set(true);
    }
  }

  onInputBlur(): void {
    // Solo cerrar si el mouse no está en las sugerencias
    if (!this.isMouseInSuggestions) {
      this.blurTimeout = setTimeout(() => {
        this.showSuggestions.set(false);
      }, 150); // Delay más corto pero suficiente
    }
  }

  onSuggestionsMouseEnter(): void {
    this.isMouseInSuggestions = true;
    // Limpiar timeout si existe
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  onSuggestionsMouseLeave(): void {
    this.isMouseInSuggestions = false;
    // Dar un poco de tiempo antes de cerrar
    this.blurTimeout = setTimeout(() => {
      this.showSuggestions.set(false);
    }, 200);
  }

  onSuggestionClick(category: Category): void {
    this.searchValue.set(category.name);
    this.showSuggestions.set(false);
    this.searchChange.emit(category.name);

    // Navegar a la página de la categoría
    this.router.navigate(['/repuestos', category.slug]);
  }

  onSearchSubmit(): void {
    if (this.searchValue().trim()) {
      this.showSuggestions.set(false);
      // Aquí podrías implementar la búsqueda general si es necesario
    }
  }

  closeSearchOverlay(): void {
    this.showSuggestions.set(false);
    // Limpiar timeout si existe
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  // Método para manejar clics en controles sin cerrar sugerencias
  onControlClick(event: Event): void {
    // Prevenir que se cierre las sugerencias
    event.stopPropagation();

    // Limpiar timeout si existe
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  // Métodos para cantidades visuales (sin afectar el carrito)
  getVisualQuantity(categoryId: number): number {
    const visualQuantity = this.visualQuantities()[categoryId];

    // Si está en el carrito, usar la cantidad del carrito
    if (visualQuantity > 0) {
      return visualQuantity;
    }

    // Si no está en el carrito, usar la cantidad visual o 1 por defecto
    return visualQuantity || 1;
  }

  incrementVisualQuantity(categoryId: number): void {
    const currentVisual = this.visualQuantities()[categoryId] || 1;
    this.visualQuantities.update((quantities) => ({
      ...quantities,
      [categoryId]: currentVisual + 1,
    }));
  }

  decrementVisualQuantity(categoryId: number): void {
    const currentVisual = this.visualQuantities()[categoryId] || 1;
    if (currentVisual > 1) {
      this.visualQuantities.update((quantities) => ({
        ...quantities,
        [categoryId]: currentVisual - 1,
      }));
    }
  }

  // Método para verificar si una categoría es hoja (última del árbol)
  isLeafCategory(category: Category): boolean {
    return this.categoriesService.isLeafCategory(category);
  }

  addToCart(category: Category): void {
    this.cartService.addItemToCart({
      categoria: category,
      cantidad: 1,
    });
    this.notificationService.showSuccess(
      `${smartTitle(category.name)} agregado al carrito`,
      'Producto disponible para cotización'
    );
  }

  removeFromCart(idDetalle: number): void {
    this.cartService.removeItemFromCart(idDetalle);
    this.notificationService.showInfo('Producto removido del carrito');
  }

  updateQuantity(idDetalle: number, newQuantity: number): void {
    this.cartService.updateItemInCartFromId(idDetalle, newQuantity);
    this.notificationService.showSuccess(
      `Cantidad actualizada: ${newQuantity}`,
      'Producto actualizado en el carrito'
    );
  }

  trackByCategory(index: number, category: Category): string {
    return category.slug;
  }

  getParentCategory(categorySlug: string): Category | null {
    return this.categoriesService.getParentCategory(categorySlug);
  }

  isInCart(idDetalle: number): boolean {
    return this.cartService.isItemInCart(idDetalle);
  }
}
